import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { UserContext } from 'providers/userProvider';
import { routes } from 'router/routes';
import type { TUser } from 'containers/app/trainer/models';

import { APP_STORE_URL, PLAY_STORE_URL, SIGN_IN_PATHS, SUPPORT_EMAIL } from '../constants';
import { readFlow } from '../flow';
import { Actions, BigAnchor, BigButton, BigLink, Frame, LinkRow, Messages, TextLink, Title } from '../ui';

/** Dead ends, each with a way out. */

export const ExpiredScreen = () => (
  <Frame>
    <Title
      icon="link_off"
      tone="warn"
      title="This link has expired"
      sub="Reset links work for 1 hour and invite links for 7 days, and each works once. Ask for a new one below."
    />
    <Actions>
      <BigLink to={SIGN_IN_PATHS.FORGOT}>Send me a new reset link</BigLink>
    </Actions>
    <Messages info="Invite links: ask the person who invited you to send it again from Team (or StrengthX support for coach accounts)." />
    <LinkRow>
      <TextLink to={SIGN_IN_PATHS.SIGN_IN}>Back to sign in</TextLink>
    </LinkRow>
  </Frame>
);

const firstNameOf = (name: string) => name.split(/\s+/)[0];

export const PausedScreen = () => {
  const [{ coachName, isTeamMember }] = useState(readFlow);
  const team = !!isTeamMember;
  const sub = team
    ? `${coachName ? `${coachName}’s` : 'Your coach’s'} StrengthX account is paused, so the team can’t sign in for now. Programs, chats and client history are kept.`
    : 'You and your clients can’t sign in for now. Your programs, chats and client history are kept.';
  const info = team
    ? `To restore access, ${coachName ? `ask ${firstNameOf(coachName)}` : 'ask your coach'} to get in touch with StrengthX support.`
    : `To restore access, email ${SUPPORT_EMAIL} from the address on your account.`;
  return (
    <Frame>
      <Title icon="pause_circle" title={team ? 'This team’s account is paused' : 'Your coach account is paused'} sub={sub} />
      <Messages info={info} />
      <Actions>
        <BigAnchor href={`mailto:${SUPPORT_EMAIL}`} icon="mail">Email StrengthX support</BigAnchor>
        <BigLink to={SIGN_IN_PATHS.SIGN_IN} secondary>Sign in with a different account</BigLink>
      </Actions>
    </Frame>
  );
};

export const TeamRemovedScreen = () => {
  const [{ coachName }] = useState(readFlow);
  return (
    <Frame>
      <Title
        icon="group_off"
        title="You’re no longer on this team"
        sub={coachName
          ? `${coachName} removed you from their team, so you can’t see their clients any more.`
          : 'You were removed from the team, so you can’t see its clients any more.'}
      />
      <Messages info={coachName ? `If this is a mistake, ask ${firstNameOf(coachName)} to invite you again.` : 'If this is a mistake, ask the head coach to invite you again.'} />
      <Actions>
        <BigLink to={SIGN_IN_PATHS.SIGN_IN} secondary>Sign in with a different account</BigLink>
      </Actions>
    </Frame>
  );
};

export const ClientScreen = () => {
  const [{ email }] = useState(readFlow);
  return (
    <Frame>
      <Title
        icon="smartphone"
        title="Use the StrengthX app"
        sub={`${email ? `${email} is` : 'This is'} a client account. This site is for coaches; your training, chat and check-ins are in the app.`}
      />
      <Actions>
        <BigAnchor href={APP_STORE_URL} icon="phone_iphone" newTab>Download on the App Store</BigAnchor>
        <BigAnchor href={PLAY_STORE_URL} icon="android" newTab>Get it on Google Play</BigAnchor>
      </Actions>
      <LinkRow>
        <TextLink to={SIGN_IN_PATHS.SIGN_IN}>I’m a coach, use a different account</TextLink>
      </LinkRow>
    </Frame>
  );
};

export const SignedOutScreen = () => (
  <Frame>
    <Title
      icon="logout"
      title="You’ve been signed out"
      sub="For your security we sign you out after 30 days, or when your password changes. Anything you’d saved is kept."
    />
    <Actions>
      <BigLink to={SIGN_IN_PATHS.SIGN_IN}>Sign in again</BigLink>
    </Actions>
  </Frame>
);

/** Where a fresh sign-in lands: the new portal for those with it, else the current one. */
export const portalFor = (user: TUser): string => {
  if (!user.isTemporaryPasswordChanged) {
    return routes.FIRST_LOG_IN;
  }
  return user.features?.includes('new-portal') ? '/new' : '/';
};

/** How long "You're signed in" shows before moving on. */
export const WELCOME_MS = 1200;

export const WelcomeScreen = () => {
  const { user } = useContext(UserContext);
  const history = useHistory();
  const to = user ? portalFor(user) : null;

  useEffect(() => {
    if (!to) {
      return undefined;
    }
    const timer = setTimeout(() => history.replace(to), WELCOME_MS);
    return () => clearTimeout(timer);
  }, [history, to]);

  if (!to) {
    return <Redirect to={SIGN_IN_PATHS.SIGN_IN} />;
  }
  return (
    <Frame>
      <Title icon="check" tone="ok" title="You’re signed in" sub="Taking you to your home page…" />
      <Actions>
        <BigButton onClick={() => history.replace(to)}>Open the coach portal</BigButton>
      </Actions>
    </Frame>
  );
};
